/* ============================================
   WikiArticleEditor — Split-pane Markdown Editor
   Left=textarea with toolbar, Right=live preview.
   ============================================ */

import { useState, useMemo, useCallback } from 'react'
import { marked } from 'marked'
import { sanitizeHtml } from '@/lib/utils'
import WikiMermaidRenderer from './WikiMermaidRenderer'
import type { WikiArticulo, WikiCategoria } from '@/hooks/useWiki'

/* ── Inline SVG Icons ── */
const icons = {
  bold: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" /><path d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
    </svg>
  ),
  italic: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" />
      <line x1="15" y1="4" x2="9" y2="20" />
    </svg>
  ),
  link: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  ),
  code: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
    </svg>
  ),
  table: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  ),
  diagram: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="6" height="5" rx="1" /><rect x="16" y="3" width="6" height="5" rx="1" />
      <rect x="9" y="16" width="6" height="5" rx="1" /><path d="M5 8v3a2 2 0 002 2h10a2 2 0 002-2V8" />
      <line x1="12" y1="13" x2="12" y2="16" />
    </svg>
  ),
  h2: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h8" /><path d="M4 18V6" /><path d="M12 18V6" />
      <path d="M17 12a2 2 0 012-2 2 2 0 012 2c0 1.5-3 2.5-4 4h4" />
    </svg>
  ),
  h3: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h8" /><path d="M4 18V6" /><path d="M12 18V6" />
      <path d="M17.5 10.5a1.5 1.5 0 011.5 1.5 1.5 1.5 0 01-1.5 1.5" />
      <path d="M17.5 13.5a1.5 1.5 0 011.5 1.5 1.5 1.5 0 01-1.5 1.5H17" />
    </svg>
  ),
  save: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  x: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  eye: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ),
  edit: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
}

interface WikiArticleEditorProps {
  articulo: WikiArticulo | null
  categorias: WikiCategoria[]
  onSave: (data: {
    titulo: string
    slug: string
    contenido: string
    resumen: string | null
    categoria_id: string
    estado: 'borrador' | 'publicado' | 'archivado'
  }) => void
  onCancel: () => void
}

/* ── Generate slug from title ── */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

/* ── Extract mermaid blocks for preview ── */
function extractMermaidBlocks(markdown: string): {
  cleanedMarkdown: string
  mermaidBlocks: { id: string; chart: string }[]
} {
  const mermaidBlocks: { id: string; chart: string }[] = []
  let counter = 0

  const cleanedMarkdown = markdown.replace(
    /```mermaid\s*\n([\s\S]*?)```/g,
    (_match, chart: string) => {
      const id = `editor-mermaid-${counter++}`
      mermaidBlocks.push({ id, chart: chart.trim() })
      return `<div data-mermaid-id="${id}"></div>`
    }
  )

  return { cleanedMarkdown, mermaidBlocks }
}

export default function WikiArticleEditor({
  articulo,
  categorias,
  onSave,
  onCancel,
}: WikiArticleEditorProps) {
  const [titulo, setTitulo] = useState(articulo?.titulo || '')
  const [contenido, setContenido] = useState(articulo?.contenido || '')
  const [resumen, setResumen] = useState(articulo?.resumen || '')
  const [categoriaId, setCategoriaId] = useState(articulo?.categoria_id || (categorias[0]?.id ?? ''))
  const [estado, setEstado] = useState<'borrador' | 'publicado' | 'archivado'>(articulo?.estado || 'borrador')
  const [activePane, setActivePane] = useState<'editor' | 'preview' | 'split'>('split')

  const textareaRef = useCallback((node: HTMLTextAreaElement | null) => {
    if (node) {
      node.focus()
    }
  }, [])

  /* ── Toolbar actions ── */
  const insertAtCursor = (before: string, after: string = '', placeholder: string = '') => {
    const textarea = document.querySelector('.wiki-editor__textarea') as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selected = contenido.substring(start, end)
    const text = selected || placeholder

    const newContent =
      contenido.substring(0, start) +
      before +
      text +
      after +
      contenido.substring(end)

    setContenido(newContent)

    // Restore cursor position
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + text.length
      )
    })
  }

  const toolbarActions = [
    { icon: icons.h2, label: 'H2', action: () => insertAtCursor('\n## ', '\n', 'Encabezado') },
    { icon: icons.h3, label: 'H3', action: () => insertAtCursor('\n### ', '\n', 'Subencabezado') },
    { divider: true },
    { icon: icons.bold, label: 'Negrita', action: () => insertAtCursor('**', '**', 'texto') },
    { icon: icons.italic, label: 'Cursiva', action: () => insertAtCursor('*', '*', 'texto') },
    { icon: icons.link, label: 'Enlace', action: () => insertAtCursor('[', '](url)', 'texto del enlace') },
    { icon: icons.code, label: 'Código', action: () => insertAtCursor('\n```\n', '\n```\n', 'código') },
    { divider: true },
    { icon: icons.table, label: 'Tabla', action: () => insertAtCursor(
      '\n| Columna 1 | Columna 2 | Columna 3 |\n|---|---|---|\n| Dato 1 | Dato 2 | Dato 3 |\n',
      '',
      ''
    )},
    { icon: icons.diagram, label: 'Mermaid', action: () => insertAtCursor(
      '\n```mermaid\nflowchart TD\n    A[Inicio] --> B{Decisión}\n    B -->|Sí| C[Resultado 1]\n    B -->|No| D[Resultado 2]\n```\n',
      '',
      ''
    )},
  ]

  /* ── Preview HTML ── */
  const { previewHtml, mermaidBlocks } = useMemo(() => {
    if (!contenido.trim()) return { previewHtml: '', mermaidBlocks: [] }

    const { cleanedMarkdown, mermaidBlocks } = extractMermaidBlocks(contenido)

    const rawHtml = marked.parse(cleanedMarkdown, {
      gfm: true,
      breaks: true,
    }) as string

    return { previewHtml: sanitizeHtml(rawHtml), mermaidBlocks }
  }, [contenido])

  /* ── Render preview with mermaid ── */
  const renderPreview = () => {
    if (!previewHtml.trim()) {
      return (
        <div className="wiki-editor__preview-empty">
          Comienza a escribir para ver la vista previa...
        </div>
      )
    }

    if (mermaidBlocks.length === 0) {
      return (
        <div className="wiki-prose" dangerouslySetInnerHTML={{ __html: previewHtml }} />
      )
    }

    const parts: React.ReactNode[] = []
    let remaining = previewHtml
    let partIdx = 0

    for (const block of mermaidBlocks) {
      const placeholder = `<div data-mermaid-id="${block.id}"></div>`
      const idx = remaining.indexOf(placeholder)

      if (idx !== -1) {
        if (idx > 0) {
          parts.push(
            <div key={`p-${partIdx}`} className="wiki-prose"
              dangerouslySetInnerHTML={{ __html: remaining.substring(0, idx) }} />
          )
        }
        parts.push(
          <WikiMermaidRenderer key={block.id} chart={block.chart} id={block.id} />
        )
        remaining = remaining.substring(idx + placeholder.length)
        partIdx++
      }
    }

    if (remaining.trim()) {
      parts.push(
        <div key={`p-${partIdx}`} className="wiki-prose"
          dangerouslySetInnerHTML={{ __html: remaining }} />
      )
    }

    return <>{parts}</>
  }

  const handleSave = () => {
    const slug = articulo?.slug || slugify(titulo)
    onSave({
      titulo: titulo.trim(),
      slug,
      contenido,
      resumen: resumen.trim() || null,
      categoria_id: categoriaId,
      estado,
    })
  }

  const isValid = titulo.trim().length > 0 && contenido.trim().length > 0 && categoriaId

  return (
    <div className="wiki-editor">
      {/* ── Top Bar ── */}
      <div className="wiki-editor__topbar">
        <div className="wiki-editor__topbar-left">
          <input
            type="text"
            className="wiki-editor__title-input"
            value={titulo}
            onChange={e => setTitulo(e.target.value)}
            placeholder="Título del artículo..."
          />
        </div>
        <div className="wiki-editor__topbar-right">
          <select
            className="form-select wiki-editor__select"
            value={categoriaId}
            onChange={e => setCategoriaId(e.target.value)}
          >
            <option value="" disabled>Categoría...</option>
            {categorias.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.nombre}</option>
            ))}
          </select>
          <select
            className="form-select wiki-editor__select"
            value={estado}
            onChange={e => setEstado(e.target.value as typeof estado)}
          >
            <option value="borrador">Borrador</option>
            <option value="publicado">Publicado</option>
            <option value="archivado">Archivado</option>
          </select>
          <button className="btn btn-secondary btn-sm" onClick={onCancel}>
            {icons.x(14)} Cancelar
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={!isValid}
          >
            {icons.save(14)} Guardar
          </button>
        </div>
      </div>

      {/* ── Resumen ── */}
      <div className="wiki-editor__resumen">
        <input
          type="text"
          className="wiki-editor__resumen-input"
          value={resumen}
          onChange={e => setResumen(e.target.value)}
          placeholder="Resumen breve del artículo (opcional)..."
        />
      </div>

      {/* ── Toolbar ── */}
      <div className="wiki-editor__toolbar">
        <div className="wiki-editor__toolbar-actions">
          {toolbarActions.map((action, i) => {
            if ('divider' in action) {
              return <div key={i} className="wiki-editor__toolbar-divider" />
            }
            return (
              <button
                key={i}
                className="wiki-editor__toolbar-btn"
                onClick={action.action}
                title={action.label}
              >
                {action.icon()}
              </button>
            )
          })}
        </div>
        <div className="wiki-editor__toolbar-views">
          <button
            className={`wiki-editor__toolbar-btn ${activePane === 'editor' ? 'wiki-editor__toolbar-btn--active' : ''}`}
            onClick={() => setActivePane('editor')}
            title="Solo editor"
          >
            {icons.edit(14)}
          </button>
          <button
            className={`wiki-editor__toolbar-btn ${activePane === 'split' ? 'wiki-editor__toolbar-btn--active' : ''}`}
            onClick={() => setActivePane('split')}
            title="Editor + Preview"
          >
            {icons.edit(14)}{icons.eye(14)}
          </button>
          <button
            className={`wiki-editor__toolbar-btn ${activePane === 'preview' ? 'wiki-editor__toolbar-btn--active' : ''}`}
            onClick={() => setActivePane('preview')}
            title="Solo preview"
          >
            {icons.eye(14)}
          </button>
        </div>
      </div>

      {/* ── Split Panes ── */}
      <div className={`wiki-editor__panes wiki-editor__panes--${activePane}`}>
        {/* Editor */}
        {(activePane === 'editor' || activePane === 'split') && (
          <div className="wiki-editor__editor-pane">
            <textarea
              ref={textareaRef}
              className="wiki-editor__textarea"
              value={contenido}
              onChange={e => setContenido(e.target.value)}
              placeholder="Escribe el contenido en Markdown..."
              spellCheck={false}
            />
          </div>
        )}

        {/* Preview */}
        {(activePane === 'preview' || activePane === 'split') && (
          <div className="wiki-editor__preview-pane">
            {renderPreview()}
          </div>
        )}
      </div>
    </div>
  )
}
